<template>
  <transition name="modal-fade">
    <div v-if="isVisible" class="modal-backdrop" @click.self="closeModal">
      <div class="modal-container">
        <header class="modal-header">
          <h2 class="node-title">
            <i :class="nodeIcon" class="mr-2"></i>
            {{ nodeLabel }}
          </h2>
          <button @click="closeModal" class="close-btn">&times;</button>
        </header>

        <section class="modal-body">
          <p v-if="nodeTypeLabel" class="node-type-badge">{{ nodeTypeLabel }}</p>
          <p class="node-description">{{ nodeDescription }}</p>
          <div v-if="isBarracksNode" class="training-roster">
            <h4>Training roster</h4>
            <ul>
              <li
                v-for="row in barracksRoster"
                :key="row.slug"
                :class="{ locked: row.locked }"
                :title="row.locked ? row.lockReasons.join(' · ') : undefined"
              >
                <i :class="row.icon" :style="{ color: row.color }"></i>
                <div class="roster-copy">
                  <span class="roster-name">
                    {{ row.name }}
                    <i v-if="row.locked" class="fas fa-lock lock-icon" aria-hidden="true"></i>
                  </span>
                  <span v-if="row.locked" class="roster-lock">{{ row.lockReasons.join(" · ") }}</span>
                  <span v-else-if="row.hp != null" class="roster-stats">
                    HP {{ row.hp }} · ATK {{ row.atk }} · DEF {{ row.def }}
                  </span>
                </div>
                <span
                  v-if="!row.locked"
                  class="roster-yield"
                  :class="row.yield > 0 ? 'live' : 'idle'"
                >+{{ row.yield }}/h</span>
              </li>
            </ul>
          </div>
          <p v-if="enableCardSocketing && isSocketNode && status === 'available'" class="socket-hint">
            Unlock this node to socket a matching card.
          </p>
          <p v-else-if="isSocketNode && status === 'available'" class="socket-hint">
            Socket node: unlocking enables this slot.
          </p>

          <div v-if="displayCost && node.cost" class="cost-section">
            <h4>Activation Cost:</h4>
            <ul class="cost-list">
              <li
                v-for="(amount, resource) in node.cost"
                :key="resource"
                :class="{ affordable: hasEnoughResource(resource, amount) }"
              >
                <i :class="getResourceIcon(resource)"></i>
                {{ formatNumber(amount) }} {{ capitalize(resource) }}
                <span v-if="!hasEnoughResource(resource, amount)">
                  (Need
                  {{ formatNumber(amount - getPlayerResource(resource)) }} more)
                </span>
              </li>
            </ul>
          </div>

          <div v-if="isHeroActivation && status === 'available'" class="hero-name-section">
            <label for="hero-name-input">Hero Name</label>
            <input
              id="hero-name-input"
              type="text"
              maxlength="24"
              autocomplete="off"
              placeholder="Letters and numbers only"
              :value="heroName"
              @input="onHeroNameInput"
            />
            <p class="name-hint">Alphanumeric only. No spaces or symbols.</p>
          </div>

          <div v-if="isCityFounding && status === 'available'" class="hero-name-section">
            <label for="city-name-input">City Name</label>
            <input
              id="city-name-input"
              type="text"
              maxlength="50"
              autocomplete="off"
              placeholder="Letters, numbers, and spaces"
              :value="cityName"
              @input="onCityNameInput"
            />
            <p class="name-hint">Alphanumeric only. Spaces are allowed.</p>
          </div>

          <p v-if="status === 'activated'" class="status-message activated">
            <i class="fas fa-check-circle"></i> Node Activated
          </p>

          <div
            v-if="enableCardSocketing && isSocketNode && status === 'activated'"
            class="socket-section"
          >
            <h4>Equipped card</h4>
            <div
              v-if="equippedCard"
              class="socket-equipped"
              :class="rarityClass(equippedCard)"
              :style="{ '--rarity-color': getRarityColor(equippedCard) }"
            >
              <div
                class="socket-art-frame"
                :class="{ 'has-art': !!equippedCard.image }"
              >
                <img
                  v-if="equippedCard.image"
                  class="socket-art"
                  :src="equippedCard.image"
                  :alt="equippedCard.name"
                />
                <div class="socket-art-copy">
                  <strong>{{ equippedCard.name }}</strong>
                  <span class="socket-meta">{{ equippedCard.rarity }} · {{ equippedCard.sub_type }}</span>
                  <p v-if="equippedStatItems.length" class="socket-stats">
                    <span
                      v-for="(item, index) in equippedStatItems"
                      :key="item.key"
                    >
                      <template v-if="index"> · </template>
                      <span
                        :class="{ dimmed: item.dimmed }"
                        :title="item.title"
                      >{{ item.text }}</span>
                    </span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                class="btn-secondary unsocket-btn"
                :disabled="isLoading"
                @click="unsocketCard"
              >
                Unsocket
              </button>
            </div>
            <p v-else class="socket-empty">No card equipped.</p>

            <h4>Compatible cards</h4>
            <p v-if="pickerCards.length === 0" class="socket-empty">
              No matching cards for this socket.
            </p>
            <ul v-else class="socket-picker">
              <li
                v-for="card in pickerCards"
                :key="card.id"
                :class="rarityClass(card)"
                :style="{ '--rarity-color': getRarityColor(card) }"
              >
                <img
                  v-if="card.image"
                  class="socket-thumb"
                  :src="card.image"
                  :alt="card.name"
                />
                <div class="socket-picker-copy">
                  <strong>{{ card.name }}</strong>
                  <span v-if="pickerStatItems(card).length" class="socket-stats">
                    <span
                      v-for="(item, index) in pickerStatItems(card)"
                      :key="item.key"
                    >
                      <template v-if="index"> · </template>
                      <span
                        :class="{ dimmed: item.dimmed }"
                        :title="item.title"
                      >{{ item.text }}</span>
                    </span>
                  </span>
                </div>
                <button
                  type="button"
                  class="btn-primary"
                  :disabled="isLoading"
                  @click="socketCard(card.id)"
                >
                  Socket
                </button>
              </li>
            </ul>
          </div>
          <p v-if="status === 'disabled'" class="status-message disabled">
            <i class="fas fa-lock"></i> Requires prerequisite nodes
          </p>

          <p v-if="displayError" class="error-message">
            {{ displayError }}
          </p>
        </section>

        <footer class="modal-footer" v-if="status === 'available' || showSocketActions">
          <button @click="closeModal" class="btn-secondary">Cancel</button>
          <button
            v-if="status === 'available'"
            @click="confirmUnlock"
            class="btn-primary activate-btn"
            :disabled="!canActivate || isLoading"
          >
            <i v-if="isLoading" class="fas fa-spinner fa-spin mr-2"></i>
            {{ isLoading ? "Activating..." : "Activate Node" }}
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useGameStore } from "../store/game";
import { formatSocketStatItems } from "../utils/cardStats";
import { playSound } from "../utils/audio";

const props = defineProps({
  isVisible: Boolean,
  nodeData: {
    // Contains { node, status }
    type: Object,
    default: null,
  },
  isLoading: Boolean,
  errorMessage: String,
  playerRace: {
    type: String,
    default: "Eldorians",
  },
  enableCardSocketing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close", "confirm-unlock", "socket-card", "unsocket-card"]);

const gameStore = useGameStore();
const localError = ref("");
const heroName = ref("");
const cityName = ref("");

const HERO_NAME_RE = /^[A-Za-z0-9]+$/;
const CITY_NAME_RE = /^[A-Za-z0-9]+(?: [A-Za-z0-9]+)*$/;

watch(
  () => props.isVisible,
  (visible) => {
    if (!visible) {
      heroName.value = "";
      cityName.value = "";
      localError.value = "";
    }
  }
);

const displayError = computed(() => props.errorMessage || localError.value);

const isHeroActivation = computed(() => {
  const key = node.value?.node_key || node.value?.id;
  return key === "Hero" && !gameStore.activeHero;
});

const isCityFounding = computed(() => {
  const key = node.value?.node_key || node.value?.id;
  return key === "FoundCity" && !gameStore.activeCity;
});

const isValidHeroName = computed(() => HERO_NAME_RE.test(heroName.value));
const isValidCityName = computed(() => CITY_NAME_RE.test(cityName.value.trim()));

// --- Computed Properties for Display ---

const node = computed(() => props.nodeData?.node);
const status = computed(() => props.nodeData?.status);

const nodeLabel = computed(() => {
  return (
    node.value?.labels?.[props.playerRace]?.label || node.value?.id || "Node"
  );
});

const nodeDescription = computed(() => {
  return (
    node.value?.labels?.[props.playerRace]?.description ||
    "Details about this node."
  );
});

const nodeIcon = computed(() => {
  if (!node.value) return "fas fa-question-circle";
  return node.value.node_type === "socket"
    ? "fas fa-drafting-compass"
    : "fas fa-star";
});

const isSocketNode = computed(() => node.value?.node_type === "socket");

const isBarracksNode = computed(() => {
  const key = node.value?.node_key || node.value?.id;
  return key === "Barracks";
});

const barracksRoster = computed(() => gameStore.activeCityBarracksRoster || []);

const showSocketActions = computed(
  () =>
    props.enableCardSocketing &&
    isSocketNode.value &&
    status.value === "activated"
);

const nodeTypeLabel = computed(() => {
  if (!node.value?.node_type) return "";
  if (node.value.node_type === "socket") {
    return node.value.socket_type
      ? `Socket · ${node.value.socket_type}`
      : "Socket";
  }
  return "Status / benefit";
});

const displayCost = computed(() => status.value === "available");

// --- Resource & Cost Logic ---

const spendableResources = computed(() => gameStore.spendableResources || {});

const resourceDisplayMap = {
  gold: { name: "Gold", icon: "fas fa-coins" },
  essence: { name: "Essence", icon: "fas fa-tint" },
  food: { name: "Food", icon: "fas fa-bread-slice" },
  wood: { name: "Wood", icon: "fas fa-tree" },
  stone: { name: "Stone", icon: "fas fa-mountain" },
  gems: { name: "Gem", icon: "fas fa-gem" },
  population: { name: "Population", icon: "fas fa-users" },
  iron: { name: "Iron", icon: "fas fa-shield-alt" }, // Example for iron
};

const getResourceIcon = (resourceName) => {
  return resourceDisplayMap[resourceName]?.icon || "fas fa-question-circle";
};

const getPlayerResource = (resourceName) => {
  return spendableResources.value?.[resourceName] || 0;
};

const hasEnoughResource = (resourceName, requiredAmount) => {
  return getPlayerResource(resourceName) >= requiredAmount;
};

const canAfford = computed(() => {
  if (!node.value?.cost) return false;
  for (const resourceName in node.value.cost) {
    if (!hasEnoughResource(resourceName, node.value.cost[resourceName])) {
      return false;
    }
  }
  return true;
});

const canActivate = computed(() => {
  if (!canAfford.value) return false;
  if (isHeroActivation.value && !isValidHeroName.value) return false;
  if (isCityFounding.value && !isValidCityName.value) return false;
  return true;
});

// --- Actions ---

const onHeroNameInput = (event) => {
  heroName.value = String(event.target.value || "").replace(/[^A-Za-z0-9]/g, "");
  localError.value = "";
};

const onCityNameInput = (event) => {
  cityName.value = String(event.target.value || "").replace(/[^A-Za-z0-9 ]/g, "");
  localError.value = "";
};

const closeModal = () => {
  localError.value = "";
  heroName.value = "";
  cityName.value = "";
  emit("close");
};

const confirmUnlock = async () => {
  localError.value = "";
  if (!canActivate.value) {
    if (isHeroActivation.value && !isValidHeroName.value) {
      localError.value = "Enter a hero name using letters and numbers only.";
    } else if (isCityFounding.value && !isValidCityName.value) {
      localError.value = "Enter a city name using letters, numbers, and spaces only.";
    }
    return;
  }
  let extra;
  if (isHeroActivation.value) extra = { heroName: heroName.value };
  else if (isCityFounding.value) extra = { cityName: cityName.value.trim().replace(/ +/g, " ") };
  emit("confirm-unlock", node.value.id, extra);
};

const RARITY_COLORS = {
  common: "#718096",
  uncommon: "#38A169",
  rare: "#3182CE",
  epic: "#805AD5",
  legendary: "#D69E2E",
};

function cardRarity(card) {
  return String(
    card?.instance_data?.rarity || card?.rarity || "common"
  ).toLowerCase();
}

function getRarityColor(card) {
  return RARITY_COLORS[cardRarity(card)] || RARITY_COLORS.common;
}

function rarityClass(card) {
  return `rarity-${cardRarity(card)}`;
}

const equippedStatItems = computed(() =>
  equippedCard.value
    ? formatSocketStatItems(equippedCard.value, {
        unlockedNodes: gameStore.unlockedCityNodeKeys,
      })
    : []
);

function pickerStatItems(card) {
  return formatSocketStatItems(card, {
    unlockedNodes: gameStore.unlockedCityNodeKeys,
  });
}

const equippedSocket = computed(() => {
  const key = node.value?.node_key || node.value?.id;
  if (!key) return null;
  const heroRow = (gameStore.heroSockets || []).find((row) => row.node_key === key);
  if (heroRow) return heroRow;
  return (gameStore.citySockets || []).find((row) => row.node_key === key) || null;
});

const equippedCard = computed(() => {
  const row = equippedSocket.value;
  if (!row) return null;
  return gameStore.ownedCards.find((card) => card.id === row.player_card_id) || null;
});

const pickerCards = computed(() => {
  if (!node.value) return [];
  return gameStore.socketableCards(node.value);
});

const socketCard = (playerCardId) => {
  const key = node.value?.node_key || node.value?.id;
  if (!key || !playerCardId) return;
  playSound("socket_effect.mp3");
  emit("socket-card", { nodeKey: key, playerCardId });
};

const unsocketCard = () => {
  const key = node.value?.node_key || node.value?.id;
  if (!key) return;
  emit("unsocket-card", { nodeKey: key });
};

// --- Helpers ---
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num;
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.modal-container {
  width: 100%;
  max-width: 550px; /* Slightly wider */
  max-height: min(88vh, 88dvh);
  background-color: #0a0a14;
  border: 1px solid #00f6ff;
  border-radius: 10px;
  box-shadow: 0 0 30px rgba(0, 246, 255, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 1rem;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 246, 255, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}
.node-title {
  margin: 0;
  text-align: left;
  font-family: "Orbitron", sans-serif;
  color: #00f6ff;
  text-shadow: 0 0 10px #00f6ff;
  font-size: 1.5rem;
}
.close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  color: #a9b1d6;
  cursor: pointer;
  padding: 0 0.5rem;
  line-height: 1;
}
.close-btn:hover {
  color: #ff00ff;
}

.modal-body {
  padding: 1.5rem;
  color: #c0c0c0;
  line-height: 1.6;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding-right: calc(1.5rem + 0.25rem);
}
.node-description {
  margin-bottom: 1.5rem;
  font-style: italic;
  color: #a9b1d6;
}

.training-roster {
  margin: 0 0 1.5rem;
}

.training-roster h4 {
  margin: 0 0 0.6rem;
  color: #00f6ff;
  font-family: "Orbitron", sans-serif;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.training-roster ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.training-roster li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
}

.training-roster li.locked {
  opacity: 0.5;
  filter: grayscale(1);
}

.training-roster li i:first-child {
  width: 22px;
  text-align: center;
  font-size: 1.05rem;
}

.roster-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.roster-name {
  color: #d8d0c0;
  font-size: 0.9rem;
}

.lock-icon {
  margin-left: 6px;
  font-size: 0.7rem;
  color: #8892b0;
}

.roster-lock,
.roster-stats {
  font-size: 0.75rem;
  color: #8892b0;
}

.roster-yield {
  font-weight: 700;
  font-size: 0.85rem;
}

.roster-yield.live {
  color: #7dffa3;
}

.roster-yield.idle {
  color: #7a8490;
}

.node-type-badge {
  display: inline-block;
  margin: 0 0 0.75rem;
  padding: 0.2rem 0.6rem;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #00f6ff;
  border: 1px solid rgba(0, 246, 255, 0.4);
  border-radius: 4px;
}

.socket-hint {
  margin: -0.5rem 0 1.25rem;
  font-size: 0.9rem;
  color: #a9b1d6;
}

.socket-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 246, 255, 0.2);
}

.socket-section h4 {
  margin: 0 0 0.6rem;
  color: #00f6ff;
  font-family: "Orbitron", sans-serif;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.socket-equipped,
.socket-picker li {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.85rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(0, 246, 255, 0.25);
  border-radius: 6px;
}

.socket-equipped.rarity-common,
.socket-picker li.rarity-common {
  --rarity-color: #718096;
}
.socket-equipped.rarity-uncommon,
.socket-picker li.rarity-uncommon {
  --rarity-color: #38a169;
}
.socket-equipped.rarity-rare,
.socket-picker li.rarity-rare {
  --rarity-color: #3182ce;
}
.socket-equipped.rarity-epic,
.socket-picker li.rarity-epic {
  --rarity-color: #805ad5;
}
.socket-equipped.rarity-legendary,
.socket-picker li.rarity-legendary {
  --rarity-color: #d69e2e;
}

.socket-equipped[class*="rarity-"],
.socket-picker li[class*="rarity-"] {
  border: 1.5px solid var(--rarity-color);
  box-shadow: 0 0 8px color-mix(in srgb, var(--rarity-color) 25%, transparent);
}

.socket-art-frame {
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  min-height: 96px;
}

.socket-art-frame.has-art {
  background: #0a1018;
}

.socket-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0.48;
}

.socket-art-copy {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem;
  background: linear-gradient(
    180deg,
    rgba(8, 12, 20, 0.72) 0%,
    rgba(8, 12, 20, 0.28) 100%
  );
}

.socket-picker {
  list-style: none;
  padding: 0;
  margin: 0;
}

.socket-picker li {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.socket-thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  object-position: center;
  border-radius: 6px;
  flex-shrink: 0;
  opacity: 0.9;
  border: 1px solid rgba(0, 246, 255, 0.25);
}

.socket-picker-copy {
  flex: 1;
  min-width: 0;
}

.socket-meta,
.socket-stats {
  display: block;
  font-size: 0.8rem;
  color: #73daca;
}

.socket-stats .dimmed {
  opacity: 0.45;
  color: #8892b0;
}

.socket-meta {
  color: #88c0d0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.socket-empty {
  margin: 0 0 1rem;
  font-style: italic;
  color: #8892b0;
}

.unsocket-btn {
  align-self: flex-start;
  margin-top: 0.35rem;
}

.cost-section h4 {
  color: #ff00ff;
  margin-top: 0;
  margin-bottom: 0.75rem;
  font-family: "Orbitron", sans-serif;
}
.cost-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
}
.cost-list li {
  margin-bottom: 0.5rem;
  color: #ff4d4d; /* Default to red (not affordable) */
  font-weight: bold;
}
.cost-list li i {
  margin-right: 0.5rem;
  width: 1.2em; /* Ensure icons align nicely */
  text-align: center;
}
.cost-list li.affordable {
  color: #73daca; /* Teal color for affordable */
}
.cost-list li span {
  font-style: italic;
  font-size: 0.85em;
  margin-left: 0.5rem;
}

.hero-name-section {
  margin: 0 0 1.5rem;
}

.hero-name-section label {
  display: block;
  font-family: "Orbitron", sans-serif;
  color: #00f6ff;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
}

.hero-name-section input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.65rem 0.75rem;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(0, 246, 255, 0.4);
  border-radius: 4px;
  color: #fff;
  font-size: 1rem;
}

.hero-name-section input:focus {
  outline: none;
  border-color: #00f6ff;
  box-shadow: 0 0 8px rgba(0, 246, 255, 0.35);
}

.name-hint {
  margin: 0.4rem 0 0;
  font-size: 0.8rem;
  color: #8892b0;
}

.status-message {
  font-weight: bold;
  text-align: center;
  margin: 1.5rem 0;
  font-size: 1.1rem;
}
.status-message.activated {
  color: #73daca; /* Teal */
}
.status-message.disabled {
  color: #ff4d4d; /* Red */
}
.status-message i {
  margin-right: 0.5rem;
}

.error-message {
  color: #ff4d4d;
  font-weight: bold;
  text-align: center;
  margin-top: 1rem;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid rgba(0, 246, 255, 0.3);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  flex-shrink: 0;
}

.btn-primary {
  /* Inherits global button styles */
}

.activate-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  filter: grayscale(0.4);
}
.activate-btn i {
  /* Spinner icon */
  margin-right: 0.5rem;
}

.btn-secondary {
  /* Inherits global button styles */
  background-color: transparent;
  border-color: #556c88;
  color: #a9b1d6;
  box-shadow: none;
}
.btn-secondary:hover {
  background-color: rgba(169, 177, 214, 0.2);
  border-color: #a9b1d6;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
